import { create } from 'zustand';
import type {
  AddressInput,
  AdminOrderQuoteDto,
  BillingInput,
  CustomerDetailDto,
  RefundMethod,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import type { EditableLine } from '@/components/order-lines-editor';
import { EMPTY_ADDRESS, addressComplete } from '@/components/address-fields';

export type ReceivedMethod = Exclude<RefundMethod, 'STORE_CREDIT'>;

/**
 * An order staff are writing for a customer — a phone or WhatsApp order (ADR-062).
 *
 * Held here, not on the server, until it is placed: a draft nobody finished is
 * simply discarded, and nothing was claimed for it. Every figure on screen is
 * the API's quote for what has been typed so far; this store never adds money
 * up (ADR-004).
 */

export type NewOrderPayment = 'COD' | 'MANUAL' | 'CARD';

interface NewOrderState {
  customerId: string;
  customer: CustomerDetailDto | null;
  email: string;
  lines: EditableLine[];
  address: AddressInput;
  shippingMethodId: string;
  paymentMethod: NewOrderPayment;
  /** How money paid outside the store came in — never store credit (ADR-081). */
  receivedMethod: ReceivedMethod | '';
  receivedReference: string;
  discountCode: string;
  note: string;
  staffNote: string;
  notifyCustomer: boolean;
  saveAddress: boolean;
  billing: BillingInput | null;

  quote: AdminOrderQuoteDto | null;
  quoting: boolean;
  placing: boolean;
  error: string | null;

  set: (patch: Partial<NewOrderState>) => void;
  /** A customer with an account, or none for a guest; their email and default address come along. */
  chooseCustomer: (id: string) => Promise<void>;
  requote: () => Promise<void>;
  /** The new order's id when placed, and the payment link for a card order. */
  place: () => Promise<{ id: string; paymentUrl: string | null } | null>;
  reset: () => void;
}

const BLANK = {
  customerId: '',
  customer: null,
  email: '',
  lines: [] as EditableLine[],
  address: EMPTY_ADDRESS,
  shippingMethodId: '',
  paymentMethod: 'COD' as NewOrderPayment,
  receivedMethod: '' as ReceivedMethod | '',
  receivedReference: '',
  discountCode: '',
  note: '',
  staffNote: '',
  notifyCustomer: true,
  saveAddress: false,
  billing: null,
  quote: null,
  quoting: false,
  placing: false,
  error: null,
};

/** Each quote is numbered, so an answer to an older draft never overwrites a newer one. */
let quoteRun = 0;

export const useNewOrderStore = create<NewOrderState>((set, get) => ({
  ...BLANK,

  set(patch) {
    set(patch);
  },

  async chooseCustomer(id) {
    if (!id) {
      set({ customerId: '', customer: null, saveAddress: false });
      return;
    }
    set({ customerId: id, error: null });
    try {
      const customer = await withSession(() => adminApi.customers.get(id));
      const saved = customer.addresses.find((address) => address.isDefault) ?? customer.addresses[0];
      set((state) => ({
        customer,
        email: customer.email ?? state.email,
        // A saved address starts the form; none starts it with their name and number.
        address: saved
          ? {
              fullName: saved.fullName,
              phone: saved.phone,
              line1: saved.line1,
              line2: saved.line2 ?? undefined,
              city: saved.city,
              region: saved.region,
              cityId: saved.cityId ?? undefined,
              regionId: saved.regionId ?? undefined,
              postalCode: saved.postalCode ?? undefined,
              country: saved.country || 'SA',
              district: saved.district ?? undefined,
              shortCode: saved.shortCode ?? undefined,
              directions: saved.directions ?? undefined,
            }
          : { ...EMPTY_ADDRESS, fullName: customer.name, phone: customer.phone ?? customer.lastOrderPhone ?? '' },
        saveAddress: !saved,
      }));
    } catch (error) {
      set({ error: errorMessage(error, 'Could not load this customer.') });
    }
  },

  async requote() {
    const state = get();
    if (state.lines.length === 0) {
      set({ quote: null, quoting: false });
      return;
    }
    const run = ++quoteRun;
    set({ quoting: true });
    try {
      const quote = await withSession(() =>
        adminApi.orders.quote({
          customerId: state.customerId || undefined,
          items: state.lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
          address: addressComplete(state.address) ? state.address : undefined,
          shippingMethodId: state.shippingMethodId || undefined,
          paymentMethod: state.paymentMethod,
          discountCode: state.discountCode.trim() || undefined,
        }),
      );
      if (run !== quoteRun) return;
      set({
        quote,
        quoting: false,
        error: null,
        // The method the server chose when none was, or when the one chosen no longer applies.
        shippingMethodId: quote.selectedShippingMethodId ?? '',
      });
    } catch (error) {
      if (run !== quoteRun) return;
      set({ quoting: false, error: errorMessage(error, 'Could not price this order.') });
    }
  },

  async place() {
    const state = get();
    set({ placing: true, error: null });
    try {
      const result = await withSession(() =>
        adminApi.orders.create({
          customerId: state.customerId || undefined,
          email: state.email.trim(),
          items: state.lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
          address: state.address,
          shippingMethodId: state.shippingMethodId,
          payment:
            state.paymentMethod === 'MANUAL'
              ? {
                  method: 'MANUAL',
                  received: {
                    method: state.receivedMethod || 'OTHER',
                    ...(state.receivedReference.trim() ? { reference: state.receivedReference.trim() } : {}),
                  },
                }
              : { method: state.paymentMethod },
          discountCode: state.discountCode.trim() || undefined,
          note: state.note.trim() || undefined,
          staffNote: state.staffNote.trim() || undefined,
          billing: state.billing?.name.trim() ? state.billing : undefined,
          saveAddress: state.customerId ? state.saveAddress : undefined,
          notifyCustomer: state.notifyCustomer,
        }),
      );
      set({ ...BLANK });
      return { id: result.order.id, paymentUrl: result.paymentUrl };
    } catch (error) {
      set({ placing: false, error: errorMessage(error, 'Could not place this order.') });
      return null;
    }
  },

  reset() {
    quoteRun += 1;
    set({ ...BLANK });
  },
}));
